import { HTTP_STATUSES } from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { useAttributes } from "@mibuildweb/hooks";
import { IAttributeData } from "../Attributes.types";

const filterEmptyAttributeOptions = (data: IAttributeData) => {
  return {
    ...data,
    attributeOptions: data.attributeOptions.filter(
      (attribute) => attribute.value !== ""
    ),
    categoryIds: data.categoryIds.map((c) =>
      typeof c === "string" ? c : c.id
    ),
    categories: undefined,
  };
};

const checkDuplicateOptions = (data: IAttributeData) => {
  const optionNames = data.attributeOptions
    .map((option) => option.value.trim())
    .filter((name) => name !== "");
  const hasDuplicates = new Set(optionNames).size !== optionNames.length;
  if (hasDuplicates) {
    toastService.error({ text: "Duplicate options are not allowed." });
  }
  return hasDuplicates;
};

export const useAddAttributesViewModel = ({
  goBack = () => null,
  reset = () => null,
}: IUseAddAttributesViewModelProps = {}) => {
  const { isLoading, getAttributeDetails, createAttribute, updateAttribute } =
    useAttributes();

  const fetchAttributeDetails = async (id: string) => {
    const response = await getAttributeDetails(id);
    if (response.status === HTTP_STATUSES.OK) {
      reset({
        ...response.data,
        categoryIds: response.data.categories,
      });
    }
  };

  const handleCreateAttribute = async (data: IAttributeData) => {
    const modifiedData = filterEmptyAttributeOptions(data);
    if (checkDuplicateOptions(modifiedData)) {
      return;
    }
    const response = await createAttribute(modifiedData);
    if (response.status === HTTP_STATUSES.OK) {
      goBack();
    }
  };

  const handleUpdateAttribute = async (data: IAttributeData) => {
    const modifiedData = filterEmptyAttributeOptions(data);
    if (checkDuplicateOptions(modifiedData)) {
      return;
    }
    const response = await updateAttribute(modifiedData);
    if (response.status === HTTP_STATUSES.OK) {
      goBack();
    }
  };

  return {
    isLoading,
    fetchAttributeDetails,
    handleCreateAttribute,
    handleUpdateAttribute,
  };
};

interface IUseAddAttributesViewModelProps {
  goBack?: () => void;
  reset?: (data: IAttributeData) => void;
}
